import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { storage } from "../../Services/Storage";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import Cropper from "react-easy-crop";
import getCroppedImg from "./cropImage";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Slider,
  IconButton,
} from "@mui/material";
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

/* ========= Overlays & helpers (composition) ========= */

// Mapping des overlays par rareté (fichiers servis depuis /public)
const OVERLAYS = {
  ultraRare: "../../overlays/UltraRare_overlay.png",
  rare: "../../overlays/Rare_overlay.png",
  commune: "../../overlays/Commune_overlay.png",
};

// Charge une image HTML depuis une URL
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();

    // ❗ Si tes overlays sont en /public (même origine), tu peux enlever cette ligne.
    //  img.crossOrigin = "anonymous";

    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error(`Impossible de charger l'image: ${src}`));
    img.src = src;
  });
// Convertit un Blob/File en Image HTML (via objectURL)
const blobToImage = async (blob) => {
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
};

// Compose base + overlay dans un canvas et retourne un Blob JPEG
const composeWithOverlay = async (baseFileOrBlob, overlayUrl) => {
  const baseImg = await blobToImage(baseFileOrBlob);
  const overlayImg = await loadImage(overlayUrl);

  const canvas = document.createElement("canvas");
  canvas.width = baseImg.width;
  canvas.height = baseImg.height;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(baseImg, 0, 0, canvas.width, canvas.height);
  ctx.drawImage(overlayImg, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.92)
  );
  return blob;
};

// Nettoie l'extension d'un nom de fichier
const stripExt = (name = "card") => name.replace(/\.(jpg|jpeg|png|webp)$/i, "");

/* ===================== Composant ===================== */

const AddCard = () => {
  const { idCollection } = useParams();
  const collectionName = localStorage.getItem("selectedCollectionName");
  const idOrganisation = localStorage.getItem("idOrganisation");

  const [imageSrc, setImageSrc] = useState(null); // objectURL de l'image brute sélectionnée
  const [croppedImageURL, setCroppedImageURL] = useState(null); // preview (image finale avec/sans overlay)
  const [imageFile, setImageFile] = useState(null); // fichier prêt à uploader (après crop & éventuel overlay)
  const [rawSelectedFile, setRawSelectedFile] = useState(null); // fichier brut sélectionné (pour nommage)

  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropAreaPixels, setCropAreaPixels] = useState(null);

  const [rarity, setRarity] = useState(""); // "commune" | "rare" | "ultraRare"
  const [overlayApplied, setOverlayApplied] = useState(false); // flag de sécurité

  const [openCropper, setOpenCropper] = useState(false);
  const navigate = useNavigate();

  /* --------- effets utilitaires --------- */
  useEffect(() => {
    // clean l'objectURL de preview quand il change
    return () => {
      if (croppedImageURL) URL.revokeObjectURL(croppedImageURL);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [croppedImageURL]);

  /* --------- Handlers --------- */

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      // Reset états liés à une précédente sélection
      setImageSrc(url);
      setRawSelectedFile(file);
      setImageFile(null);
      setCroppedImageURL(null);
      setOverlayApplied(false);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setOpenCropper(true); // Ouvre le cropper
      setAlertMessage("");
    }
  };

  const handleCloseCropper = () => {
    setOpenCropper(false);
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCropAreaPixels(croppedPixels);
  }, []);

  // Applique le crop (et éventuellement l'overlay si rareté déjà choisie)
  const handleCropImage = async () => {
    if (!imageSrc || !cropAreaPixels) return;

    const croppedBlob = await getCroppedImg(imageSrc, cropAreaPixels);

    let finalBlob = croppedBlob;
    let applied = false;

    // Si une rareté est déjà choisie, applique l’overlay de suite
    if (rarity && OVERLAYS[rarity]) {
      finalBlob = await composeWithOverlay(croppedBlob, OVERLAYS[rarity]);
      applied = true;
    }

    const baseName = stripExt(rawSelectedFile?.name);
    const finalFile = new File(
      [finalBlob],
      `${baseName}${rarity ? `_${rarity}` : ""}.jpg`,
      { type: "image/jpeg" }
    );

    const previewURL = URL.createObjectURL(finalFile);

    setImageFile(finalFile); // fichier final (croppé + overlay si appliqué)
    setCroppedImageURL(previewURL); // preview à l'écran
    setOverlayApplied(applied);
  };

  // Changement de rareté : compose/maj le fichier si on a déjà une image croppée
  const handleRarityChange = async (event) => {
    const value = event.target.value;
    setRarity(value);

    if (imageFile && OVERLAYS[value]) {
      const newBlob = await composeWithOverlay(imageFile, OVERLAYS[value]);
      const baseName = stripExt(imageFile.name);
      const finalFile = new File(
        [newBlob],
        `${baseName.split("_")[0]}_${value}.jpg`,
        {
          type: "image/jpeg",
        }
      );
      const previewURL = URL.createObjectURL(finalFile);
      setImageFile(finalFile);
      setCroppedImageURL(previewURL);
      setOverlayApplied(true);
    } else {
      // pas d'image à laquelle appliquer (l’utilisateur choisira la rareté, puis on appliquera à l’upload)
      setOverlayApplied(false);
    }
  };

  // Upload vers Firebase Storage (force l’overlay si nécessaire)
  const handleUpload = async () => {
    if (!imageFile) {
      setAlertMessage("Aucune image prête à être uploadée.");
      setAlertSeverity("error");
      return;
    }
    if (!rarity) {
      setAlertMessage("Choisissez une rareté pour appliquer le contour.");
      setAlertSeverity("error");
      return;
    }

    let fileToUpload = imageFile;

    // Si la rareté est définie mais que l’overlay n’a pas encore été appliqué, applique maintenant
    if (rarity && OVERLAYS[rarity] && !overlayApplied) {
      const blob = await composeWithOverlay(imageFile, OVERLAYS[rarity]);
      const baseName = stripExt(imageFile.name);
      fileToUpload = new File(
        [blob],
        `${baseName.split("_")[0]}_${rarity}.jpg`,
        {
          type: "image/jpeg",
        }
      );
      // maj preview + état
      const previewURL = URL.createObjectURL(fileToUpload);
      setImageFile(fileToUpload);
      setCroppedImageURL(previewURL);
      setOverlayApplied(true);
    }

    const storageRef = ref(
      storage,
      `images/${idCollection}/${fileToUpload.name}`
    );
    const uploadTask = uploadBytesResumable(storageRef, fileToUpload);

    setUploading(true);
    setAlertMessage("");

    uploadTask.on(
      "state_changed",
      () => {},
      (error) => {
        console.error(error);
        setUploading(false);
        setAlertMessage("Erreur lors de l'upload.");
        setAlertSeverity("error");
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setImageUrl(downloadURL); // Stocker l'URL pour la DB ensuite
        setAlertMessage("Image téléchargée avec succès !");
        setAlertSeverity("success");
        setUploading(false);
      }
    );
  };

  // Envoi de l’URL vers ton backend
  const handleSendUrlToDb = async () => {
    if (!imageUrl) {
      setAlertMessage("Aucune image n'a été téléchargée.");
      setAlertSeverity("error");
      return;
    }
    if (!rarity) {
      setAlertMessage("Rareté manquante.");
      setAlertSeverity("error");
      return;
    }

    const payload = {
      path: `organisations/${idOrganisation}/collections/${idCollection}/urls`,
      url: imageUrl,
      rarity,
      idOrganisation,
      idCollection,
    };

    try {
      const response = await fetch(
        "https://backend-betkiff.vercel.app/collections/addUrl",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      setAlertMessage(result.message || "Carte ajoutée !");
      setAlertSeverity("success");
      navigate("/collections");
    } catch (error) {
      setAlertMessage("Erreur lors de l'ajout de l'URL.");
      setAlertSeverity("error");
    }
  };

  /* ---------------------- Rendu ---------------------- */

  return (
    <Box
      sx={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "95%",
        textAlign: "center",
        padding: "40px",
        margin: "auto",
        borderRadius: "10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}>
      <IconButton
        onClick={() => navigate("/collections")}
        sx={{ position: "absolute", top: "-5vh", left: 40 }}>
        <ArrowBackIosIcon
          sx={{ color: "rgba(90, 20, 121, 0.9)", fontSize: 30 }}
        />
      </IconButton>

      <Typography variant="h4" gutterBottom>
        Collection : {collectionName || "Nom inconnu"}
      </Typography>

      {/* Aperçu */}
      <Box
        sx={{
          width: "50%",
          height: "220px",
          borderRadius: "10px",
          border: "2px dashed #ccc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          mt: 2,
        }}>
        {croppedImageURL ? (
          <img
            src={croppedImageURL}
            alt="Aperçu"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Typography variant="body1" color="gray">
            Aperçu de l'image
          </Typography>
        )}
      </Box>

      {/* Bouton sélectionner image (visible tant qu'on n'a pas uploadé) */}
      {!imageSrc && (
        <Button variant="contained" component="label" sx={{ mt: 2 }}>
          Sélectionner une image
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleImageChange}
          />
        </Button>
      )}

      {/* Sélection de rareté : n'apparaît QUE si une image a été sélectionnée */}
      {imageSrc && !imageUrl && (
        <FormControl fullWidth sx={{ mt: 2, width: "70%" }}>
          <InputLabel>Rareté de la carte</InputLabel>
          <Select
            value={rarity}
            onChange={handleRarityChange}
            label="Rareté de la carte">
            <MenuItem value="commune">Commune</MenuItem>
            <MenuItem value="rare">Rare</MenuItem>
            <MenuItem value="ultraRare">Ultra Rare</MenuItem>
          </Select>
        </FormControl>
      )}

      {/* Bouton d'upload : visible quand on a une image croppée (preview dispo) et pas encore uploadée */}
      {!imageUrl && croppedImageURL && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={uploading || !rarity}
          sx={{ mt: 2 }}>
          {uploading ? <CircularProgress size={24} /> : "Uploader l'image"}
        </Button>
      )}

      {/* Bouton d’enregistrement DB : visible quand on a une URL de storage */}
      {imageUrl && (
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSendUrlToDb}
          sx={{ mt: 2 }}>
          Enregistrer l'URL
        </Button>
      )}

      {alertMessage && (
        <Alert severity={alertSeverity} sx={{ mt: 2 }}>
          {alertMessage}
        </Alert>
      )}

      {/* Dialog de recadrage */}
      <Dialog
        open={openCropper}
        onClose={handleCloseCropper}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
        }}>
        <DialogContent sx={{ backgroundColor: "#222", color: "#fff" }}>
          <Typography variant="h6" textAlign="center">
            Recadrez votre image
          </Typography>
          <Box sx={{ position: "relative", width: "100%", height: 400 }}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={3 / 4}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </Box>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(_, v) => setZoom(v)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCropper} color="secondary">
            Annuler
          </Button>
          <Button
            onClick={() => {
              handleCropImage();
              handleCloseCropper();
            }}
            variant="contained"
            color="primary">
            Valider le recadrage
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddCard;
